import React, { useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Button, Divider, Link as MUILink } from '@mui/material'
import { Link } from '@src/components/Link'
import { sendConfiguratorUserInteractionGa4Event } from '@utils/analytics/configuratorAnalytics'
import { useAvailabilityMessageTracking } from '../TrackedAvailabilityMessage/TrackedAvailabilityMessage'
import { ConfiguratorGaEventName } from '@src/routes/Configurators/components/ConfiguratorConfiguration/enum'
import {
  EventActionComponentType,
  EventActionElementType,
  EventProductBrandType,
} from '@src/utils/analytics/enums'
import { useUserSession } from '@src/utils/useUserSession'
import { getLocalizedUrl } from '@src/utils/regional'
import {
  PricingOverrideEventPayload,
  sendPricingOverrideEvent,
} from '@src/utils/analytics'

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  paMessageContainer: {
    display: 'block',
    flexDirection: 'column',
    textAlign: 'center',
  },
  leftAlign: {
    textAlign: 'left',
  },
  contentWrapper: {
    display: 'inline-block',
    margin: theme.spacing(5, 0),
    width: '90%',
  },
  contentWrapperWithButtons: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(5, 0),
    [theme.breakpoints.up('md')]: {
      display: 'inline-block',
    },
  },
  buttonContainer: {
    margin: theme.spacing(5, 0),
  },
  button: {
    textAlign: 'center',
    color: `${theme.palette.common.white} !important`,
    '&:hover': {
      textDecoration: 'none !important',
    },
    display: 'inline-block',
    margin: theme.spacing(1, 0),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      margin: theme.spacing(0, 1),
      width: 'auto',
    },
  },
  textLink: {
    textTransform: 'lowercase',
  },
  pdpDisplayTypeButton: {
    margin: theme.spacing(1, 0),
    [theme.breakpoints.up('md')]: {
      width: '100%',
    },
  },
}))

interface ElementCollection {
  htmlLinks: (HTMLAnchorElement | HTMLAreaElement)[]
  htmlText: HTMLCollectionOf<HTMLElement | HTMLSpanElement> | []
}

export const parseHTML = (message: string): Document => {
  const parser = new DOMParser()
  return parser.parseFromString(message, 'text/html')
}

export const getElements = (htmlCollection: Document): ElementCollection => {
  const htmlLinks = [...htmlCollection.links]
  const b = htmlCollection.getElementsByTagName('b')
  const span = htmlCollection.getElementsByTagName('span')
  const htmlText = span.length > 0 ? span : b

  return { htmlLinks, htmlText }
}

export const parseLinks = (elements) => {
  return elements.htmlLinks
    .map((link) => {
      const href = link.getAttribute('href')
      return {
        href: `${!href.startsWith('/') ? '/' : ''}${href}`,
        text: link.textContent.trim(),
      }
    })
    .filter(Boolean)
}

export enum ProductPaMessageDisplayType {
  PDP = 'pdp',
  SRP = 'srp',
}

interface ProductPaMessageProps {
  brandKey: string
  paMessage: string
  productNumber: string
  displayType?: string
  leftAlign?: boolean
  gaPricingOverrides?: PricingOverrideEventPayload
}

const ProductPaMessage: React.FC<ProductPaMessageProps> = (props) => {
  const {
    brandKey,
    leftAlign,
    paMessage,
    productNumber,
    gaPricingOverrides = {},
    displayType,
  } = props
  useAvailabilityMessageTracking('PA_MESSAGE')
  const { classes, cx } = useStyles()
  const [isClient, setIsClient] = useState(false)
  const { userSession } = useUserSession()
  const pdpDisplayType = displayType === ProductPaMessageDisplayType.PDP

  const renderMessage = (elements: ElementCollection): JSX.Element | null => {
    const links = parseLinks(elements)
    const textTag = elements.htmlText[0]

    if (textTag?.localName === 'b') {
      return (
        <div className={classes.contentWrapper}>
          <b>{textTag.textContent} </b>
          <span>{textTag.nextSibling?.textContent}</span>
          <span>
            <Link href={links[0].href} passHref>
              <MUILink
                className={classes.textLink}
                color="primary"
                data-testid={`pa-message-link-${brandKey}-${productNumber}`}
              >
                {links[0].text}
              </MUILink>
            </Link>
          </span>
        </div>
      )
    }
    if (textTag?.localName === 'span') {
      return (
        <div className={classes.contentWrapperWithButtons}>
          <span>{textTag.textContent}</span>
          <div className={classes.buttonContainer}>
            {links.map((link) => {
              return (
                <Button
                  key={link.text}
                  data-testid={`pa-message-button-${brandKey}-${productNumber}`}
                  variant="contained"
                  color="primary"
                  href={link.href}
                  className={cx(classes.button, {
                    [classes.pdpDisplayTypeButton]: pdpDisplayType,
                  })}
                  onClick={() => {
                    if (
                      link.href === '/lab-water' ||
                      link.href.includes('/campaigns/milli-q-iq')
                    ) {
                      const localizedUrl = getLocalizedUrl(
                        userSession,
                        link.href
                      )

                      const elementText =
                        link.href === '/lab-water'
                          ? 'select and configure system'
                          : 'contact specialist'
                      const linkUrl =
                        link.href === '/lab-water'
                          ? localizedUrl.as + '?step=1'
                          : localizedUrl.as
                      sendConfiguratorUserInteractionGa4Event({
                        event:
                          ConfiguratorGaEventName.Lab_Water_Configurator_Interaction,
                        action: elementText,
                        component: EventActionComponentType.Body,
                        elementType: EventActionElementType.Button,
                        elementText,
                        linkUrl,
                        productId: productNumber,
                        productBrand: EventProductBrandType.Mm,
                      })
                    }
                    sendPricingOverrideEvent({
                      action: link.text,
                      detail: textTag?.textContent || undefined,
                      elementType: 'button',
                      elementText: link.text,
                      linkUrl: link.href,
                      productId: productNumber,
                      productBrand: brandKey,
                      ...gaPricingOverrides,
                    })
                  }}
                >
                  {link.text}
                </Button>
              )
            })}
          </div>
        </div>
      )
    }
    return null
  }

  let elements: ElementCollection = { htmlLinks: [], htmlText: [] }
  if (isClient) {
    const htmlCollection = parseHTML(paMessage)
    elements = getElements(htmlCollection)
  }

  useEffect(() => {
    setIsClient(true)
  }, [])

  return (
    <>
      {isClient ? (
        <div
          className={classes.container}
          id={`pricing-availability-message-${brandKey}-${productNumber}`}
          data-testid={`pricing-availability-message-${brandKey}-${productNumber}`}
        >
          <div
            className={cx(classes.paMessageContainer, {
              [classes.leftAlign]: leftAlign,
            })}
          >
            {!leftAlign || (!pdpDisplayType && <Divider light />)}
            {renderMessage(elements)}
          </div>
        </div>
      ) : null}
    </>
  )
}

export default ProductPaMessage
